import { InteractionType, DEBOUNCED_TYPES } from './trackInteractionTypes';
import {
  buildInteractionEvent,
  pushToGA4,
  __setGA4ConfigForTesting__,
  getScrollPercent,
  sanitizeToKebabCase,
  resolveTrackingSection,
} from './trackInteractionCore';
import { markMeaningfulInteraction } from './trackQualitySession';

const DEBOUNCE_MS = 500;
const debounceTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

// Interaction types that signal genuine user engagement (not passive clicks).
// Used by quality_session tracking to detect engaged users for ad optimization.
const MEANINGFUL_TYPES: ReadonlySet<InteractionType> = new Set([
  'calculator_interact', 'form_submit', 'form_focus', 'video_play',
]);

/**
 * Track a user interaction and send it to GA4 via the Measurement Protocol.
 *
 * For click events, prefer data attributes (`data-track-id`, `data-track-section`, `data-track-type`)
 * which are detected automatically by the global listener. Use this function directly for non-click
 * events like video play, calculator input changes, form focus, and carousel slides.
 */
export const trackInteraction = (
  id: string,
  section: string,
  type: InteractionType,
  element?: HTMLElement | null,
  value?: string
): void => {
  if (typeof window === 'undefined') return;

  if (DEBOUNCED_TYPES.has(type)) {
    // Defer everything (not just the send) until the burst settles: building
    // the event reads layout/cookies/storage, and marking the interaction as
    // meaningful should count a settled change, not every keystroke.
    const key = `${id}:${section}:${type}`;
    const existing = debounceTimers.get(key);
    if (existing) clearTimeout(existing);
    debounceTimers.set(
      key,
      setTimeout(() => {
        if (MEANINGFUL_TYPES.has(type)) {
          markMeaningfulInteraction();
        }
        pushToGA4(buildInteractionEvent(id, section, type, element, value));
        debounceTimers.delete(key);
      }, DEBOUNCE_MS)
    );
  } else {
    if (MEANINGFUL_TYPES.has(type)) {
      markMeaningfulInteraction();
    }
    pushToGA4(buildInteractionEvent(id, section, type, element, value));
  }
};

// Test helper - clears pending debounce timers so they don't fire across tests
export const __resetDebounceForTesting__ = (): void => {
  for (const timer of debounceTimers.values()) {
    clearTimeout(timer);
  }
  debounceTimers.clear();
};

// Re-export for use by the global click listener and other modules
export {
  buildInteractionEvent,
  pushToGA4,
  __setGA4ConfigForTesting__,
  getScrollPercent,
  sanitizeToKebabCase,
  resolveTrackingSection,
};
